import React, { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Money01Icon,
  BankIcon,
  Clock01Icon,
  CheckmarkCircle01Icon,
  Cancel01Icon,
  InformationCircleIcon,
  ArrowRight01Icon,
  Wallet01Icon
} from '@hugeicons/core-free-icons';
import { api } from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { FadeLoader } from 'react-spinners';
import Button from '../../components/Button';
import ResponsiveModal from '../../components/ResponsiveModal';
import ReelText from '../../components/ui/ReelText';

const ClientCommissions = () => {
  const [data, setData] = useState({ earnings: [], recent_payouts: [] });
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({ current_page: 1, last_page: 1 });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [totalEarned, setTotalEarned] = useState(0);
  const [activeTab, setActiveTab] = useState('earnings'); // 'earnings' | 'history'
  const { addToast } = useToast();

  const fetchData = async () => {
    try {
      const [commRes, banksRes] = await Promise.all([
        api.get('/client/commissions'),
        api.get('/bank-accounts')
      ]);
      setData(commRes.data);
      const total = (commRes.data.earnings || []).reduce((acc, curr) => acc + parseFloat(curr.amount), 0);
      setTotalEarned(total);
      setBanks(banksRes.data.data || banksRes.data || []);
    } catch (err) {
      addToast("Failed to load earnings", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchFullHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/client/commissions/history?page=${page}`);
      const paginated = res.data?.data || res.data || [];
      setHistoryData(paginated);
      setHistoryPagination({
        current_page: res.data.current_page || 1,
        last_page: res.data.last_page || 1
      });
      setIsHistoryModalOpen(true);
    } catch (err) {
      addToast("Failed to load history", "error");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!selectedBankId) return addToast("Please select a bank account", "error");
    
    if (parseFloat(selectedAgent?.amount || 0) < 100) {
      return addToast("Minimum payout amount is ₦100.00", "error");
    }

    setRequesting(true);
    try {
      await api.post('/client/commissions/request-payout', {
        agent_id: selectedAgent.agent.uid,
        bank_account_id: selectedBankId
      });
      addToast("Payout requested! The agent will process it soon.", "success");
      setIsPayoutModalOpen(false);
      fetchData();
    } catch (err) {
      addToast(err.message || "Request failed", "error");
    } finally {
      setRequesting(false);
    }
  };

  if (loading && data.earnings.length === 0) {
    return <div className="min-h-[60vh] flex items-center justify-center"><FadeLoader color="#4ade80" /></div>;
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 max-w-4xl mx-auto pb-24 sm:pb-32">
      <div className="bg-soft-green text-white p-10 rounded-[3rem] relative overflow-hidden shadow-float-lg">
        <div className="absolute top-0 right-0 p-10 opacity-10">
          <HugeiconsIcon icon={Wallet01Icon} size={140} />
        </div>
        <div className="relative z-10">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-80 mb-2">Total Accumulated Cashback</p>
          <h1 className="text-6xl font-black tracking-tighter flex items-center gap-1">
            <span>₦</span>
            <ReelText text={totalEarned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
          </h1>
          <p className="mt-4 text-sm font-medium opacity-90 max-w-xs">Earned from your successful deposit trades across all agents.</p>
        </div>
      </div>

      <div className="flex justify-center">
        <div className="bg-off-white dark:bg-gray-800 p-1.5 rounded-2xl flex gap-1 border border-light-gray/20 dark:border-gray-700 shadow-inner w-full sm:w-auto">
          <button 
            onClick={() => setActiveTab('earnings')}
            className={`flex-1 sm:flex-none px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'earnings' ? 'bg-white dark:bg-dark-surface shadow-md text-soft-green scale-[1.02]' : 'text-muted-gray hover:text-charcoal dark:hover:text-gray-300'}`}
          >
            Earnings
          </button>
          <button 
            onClick={() => setActiveTab('history')}
            className={`flex-1 sm:flex-none px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'history' ? 'bg-white dark:bg-dark-surface shadow-md text-soft-green scale-[1.02]' : 'text-muted-gray hover:text-charcoal dark:hover:text-gray-300'}`}
          >
            History
          </button>
        </div>
      </div>

      {activeTab === 'earnings' ? (
        <div className="grid grid-cols-1 gap-4">
          {data.earnings.length > 0 ? data.earnings.map(earn => (
            <div key={earn.id} className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-[2.5rem] border border-light-gray/20 dark:border-dark-border/20 p-5 sm:p-8 flex items-center justify-between gap-4 shadow-sm hover:shadow-float transition-all">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-12 h-12 bg-soft-green/10 rounded-2xl flex items-center justify-center text-soft-green shrink-0">
                  <HugeiconsIcon icon={Money01Icon} size={24} />
                </div>
                <div className="min-w-0">
                  <p className="text-[8px] font-black text-muted-gray uppercase tracking-widest mb-0.5">From Agent</p>
                  <p className="text-sm sm:text-xl font-black text-charcoal dark:text-white truncate">
                    {earn.agent?.business_name || earn.agent?.email.split('@')[0] || 'Verified Agent'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 sm:gap-8">
                <div className="text-right">
                  <p className="text-[8px] font-black text-muted-gray uppercase tracking-widest mb-0.5">Balance</p>
                  <p className="text-lg sm:text-2xl font-black text-soft-green">
                    ₦{parseFloat(earn.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <button 
                  onClick={() => { setSelectedAgent(earn); setIsPayoutModalOpen(true); }}
                  className="p-3 bg-soft-green text-white rounded-xl shadow-lg hover:scale-105 active:scale-95 transition-all"
                >
                  <HugeiconsIcon icon={ArrowRight01Icon} size={20} />
                </button>
              </div>
            </div>
          )) : (
            <div className="p-16 text-center bg-white/5 rounded-[3rem] border border-dashed border-white/10">
              <HugeiconsIcon icon={InformationCircleIcon} size={48} className="text-muted-gray mx-auto mb-4 opacity-20" />
              <p className="text-muted-gray font-bold uppercase tracking-[0.2em] opacity-40 text-xs">No earnings yet. Start trading to earn cashback!</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-between items-center px-4">
            <h2 className="text-xl font-black text-charcoal dark:text-white">Recent Payouts</h2>
            <button 
              onClick={() => fetchFullHistory(1)}
              className="text-[10px] font-black uppercase text-soft-green hover:underline"
            >
              Full History
            </button>
          </div>
          <div className="bg-white/50 dark:bg-black/20 rounded-[2.5rem] border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-black/5 dark:bg-white/5 text-muted-gray text-[10px] uppercase font-black tracking-widest">
                  <tr>
                    <th className="px-8 py-5">Agent</th>
                    <th className="px-8 py-5">Amount</th>
                    <th className="px-8 py-5">Status</th>
                    <th className="px-8 py-5 text-right">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {data.recent_payouts.map(p => (
                    <tr key={p.uid} className="text-sm font-bold text-charcoal dark:text-white hover:bg-white/5 transition-colors">
                      <td className="px-8 py-6">
                        {p.agent?.business_name || p.agent?.email.split('@')[0] || 'Agent'}
                      </td>
                      <td className="px-8 py-6 text-soft-green">
                        ₦{parseFloat(p.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-8 py-6">
                        <span className={`flex items-center gap-1.5 text-[10px] uppercase font-black ${
                          p.status === 'completed' ? 'text-soft-green' : p.status === 'rejected' ? 'text-red-500' : 'text-yellow-500'
                        }`}>
                          <HugeiconsIcon icon={p.status === 'completed' ? CheckmarkCircle01Icon : p.status === 'rejected' ? Cancel01Icon : Clock01Icon} size={14} />
                          {p.status}
                        </span>
                      </td>
                      <td className="px-8 py-6 text-right text-muted-gray text-xs font-medium">{new Date(p.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <ResponsiveModal 
        isOpen={isPayoutModalOpen} 
        onClose={() => setIsPayoutModalOpen(false)}
        title="Select Bank for Payout"
      >
        <div className="py-6 space-y-6">
          <div className="bg-soft-green/10 p-6 rounded-3xl border border-soft-green/20 text-center">
            <p className="text-[10px] font-black text-soft-green uppercase tracking-widest mb-1">Withdrawing</p>
            <p className="text-2xl font-black text-charcoal dark:text-white flex items-center justify-center gap-1">
              <span>₦</span>
              <ReelText text={parseFloat(selectedAgent?.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
            </p>
            <p className="text-xs font-bold text-muted-gray mt-1">From {selectedAgent?.agent?.business_name}</p>
          </div>

          <div className="space-y-3">
            <label className="block text-[10px] font-black text-muted-gray uppercase tracking-widest px-1">Choose Destination Account</label>
            {banks.length === 0 ? (
              <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-2xl text-center border border-red-100 dark:border-red-800/30">
                <p className="text-sm text-red-600 dark:text-red-400 font-bold mb-4">You need to add a bank account first.</p>
                <Button onClick={() => window.location.href = '/profile'} variant="secondary" className="text-xs py-2">Go to Profile</Button>
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                {banks.map(bank => (
                  <div 
                    key={bank.id} 
                    onClick={() => setSelectedBankId(bank.id)}
                    className={`p-4 rounded-2xl border-2 cursor-pointer transition-all flex items-center justify-between ${selectedBankId === bank.id ? 'border-soft-green bg-soft-green/5' : 'border-light-gray/20 dark:border-gray-700'}`}
                  >
                    <div>
                      <p className="font-black text-charcoal dark:text-white text-sm">{bank.bank_name}</p>
                      <p className="text-[10px] text-muted-gray font-bold">{bank.account_number}</p>
                    </div>
                    {selectedBankId === bank.id && <HugeiconsIcon icon={CheckmarkCircle01Icon} size={20} className="text-soft-green" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleRequestPayout} disabled={requesting || !selectedBankId} className="w-full py-4 rounded-2xl font-black shadow-lg">
            {requesting ? 'Processing...' : 'Confirm Payout Request'}
          </Button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal 
        isOpen={isHistoryModalOpen} 
        onClose={() => setIsHistoryModalOpen(false)}
        title="Payout History"
      >
        <div className="py-4 space-y-6">
          {historyLoading ? (
            <div className="py-20 flex justify-center"><FadeLoader color="#4ade80" /></div>
          ) : (
            <>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                {historyData.map(p => (
                  <div key={p.uid} className="p-5 bg-white dark:bg-black/20 rounded-3xl border border-gray-100 dark:border-white/5 flex justify-between items-center">
                    <div className="min-w-0 flex-1">
                      <p className="font-black text-sm text-charcoal dark:text-white truncate">
                        {p.agent?.business_name || p.agent?.email?.split('@')[0] || 'Agent'}
                      </p>
                      <p className="text-[10px] text-muted-gray font-bold">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-black text-soft-green">₦{parseFloat(p.amount).toLocaleString()}</p>
                      <span className={`text-[8px] font-black uppercase tracking-widest ${p.status === 'completed' ? 'text-soft-green' : p.status === 'rejected' ? 'text-red-500' : 'text-yellow-500'}`}>{p.status}</span>
                    </div>
                  </div>
                ))}
                {historyData.length === 0 && (
                  <div className="py-10 text-center text-muted-gray italic">No history found.</div>
                )}
              </div>
              
              {historyPagination.last_page > 1 && (
                <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-white/5">
                  <button 
                    disabled={historyPagination.current_page === 1}
                    onClick={() => fetchFullHistory(historyPagination.current_page - 1)}
                    className="px-4 py-2 bg-gray-100 dark:bg-white/5 rounded-xl text-[10px] font-black uppercase disabled:opacity-30"
                  >
                    Prev
                  </button>
                  <span className="text-[10px] font-black">{historyPagination.current_page} / {historyPagination.last_page}</span>
                  <button 
                    disabled={historyPagination.current_page === historyPagination.last_page}
                    onClick={() => fetchFullHistory(historyPagination.current_page + 1)}
                    className="px-4 py-2 bg-gray-100 dark:bg-white/5 rounded-xl text-[10px] font-black uppercase disabled:opacity-30"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default ClientCommissions;
